"use client"

import { useState, useMemo } from "react"
import Link from "next/link"
import { Search, ArrowRight } from "lucide-react"
import type { Category } from "./glossary-data"
import type { Idioma } from "@/lib/landing/idioma"

// Las de glossary-data, sin importar ese fichero (pesa) en el cliente.
const categories: readonly Category[] = ["Finanzas", "Operaciones", "RRHH", "Producción", "Legal"]

const categoryColors: Record<string, { bg: string; text: string }> = {
  Finanzas: { bg: "bg-emerald-100", text: "text-emerald-700" },
  Operaciones: { bg: "bg-sky-100", text: "text-sky-700" },
  RRHH: { bg: "bg-violet-100", text: "text-violet-700" },
  Producción: { bg: "bg-orange-100", text: "text-orange-700" },
  Legal: { bg: "bg-rose-100", text: "text-rose-700" },
}

const T = {
  es: {
    buscar: "Buscar término (ej. food cost, APPCC, escandallo...)",
    todos: "Todos",
    nada: "No se encontraron términos para tu búsqueda.",
    limpiar: "Limpiar filtros",
    leer: "Leer más",
    categoria: {} as Record<string, string>,
  },
  en: {
    buscar: "Search for a term (e.g. food cost, HACCP, recipe costing...)",
    todos: "All",
    nada: "No terms match your search.",
    limpiar: "Clear filters",
    leer: "Read more",
    categoria: { Finanzas: "Finance", Operaciones: "Operations", RRHH: "People", Producción: "Kitchen", Legal: "Legal" } as Record<string, string>,
  },
}

/** Lo que la rejilla necesita de cada término: la página le pasa solo esto. */
export interface TerminoDeRejilla {
  clave: string
  href: string
  term: string
  shortDefinition: string
  keywords: string[]
  /** La clave castellana de la categoría ("Finanzas"…). */
  category: string
}

export default function GlossaryGrid({ terminos, idioma = "es" }: { terminos: TerminoDeRejilla[]; idioma?: Idioma }) {
  const t = T[idioma]
  const nombreDe = (cat: string) => t.categoria[cat] ?? cat
  const [search, setSearch] = useState("")
  const [activeCategory, setActiveCategory] = useState<Category | "all">("all")

  const filteredTerms = useMemo(() => {
    return terminos
      .filter((term) => {
        const matchesCategory =
          activeCategory === "all" || term.category === activeCategory
        const matchesSearch =
          search === "" ||
          term.term.toLowerCase().includes(search.toLowerCase()) ||
          term.shortDefinition.toLowerCase().includes(search.toLowerCase()) ||
          term.keywords.some((k) =>
            k.toLowerCase().includes(search.toLowerCase())
          )
        return matchesCategory && matchesSearch
      })
      .sort((a, b) => a.term.localeCompare(b.term, idioma))
  }, [search, activeCategory, terminos, idioma])

  const alphabet = useMemo(() => {
    const letters = new Set(
      terminos.map((t) => t.term.charAt(0).toUpperCase())
    )
    return Array.from(letters).sort((a, b) => a.localeCompare(b, idioma))
  }, [terminos, idioma])

  return (
    <>
      {/* Search & Filters */}
      <section className="py-12 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Search */}
          <div className="max-w-xl mx-auto mb-8">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
              <input
                type="text"
                placeholder={t.buscar}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-12 pr-4 py-3.5 border border-zinc-300 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent bg-white"
              />
            </div>
          </div>

          {/* Category pills */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            <button
              onClick={() => setActiveCategory("all")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeCategory === "all"
                  ? "bg-zinc-900 text-white"
                  : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
              }`}
            >
              {t.todos}
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() =>
                  setActiveCategory(activeCategory === cat ? "all" : cat)
                }
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeCategory === cat
                    ? "bg-zinc-900 text-white"
                    : `${categoryColors[cat].bg} ${categoryColors[cat].text} hover:opacity-80`
                }`}
              >
                {nombreDe(cat)}
              </button>
            ))}
          </div>

          {/* Alphabetical index */}
          <div className="flex flex-wrap justify-center gap-1 mb-12">
            {alphabet.map((letter) => (
              <a
                key={letter}
                href={`#letter-${letter}`}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium text-zinc-600 hover:bg-lime-100 hover:text-lime-700 transition-colors"
              >
                {letter}
              </a>
            ))}
          </div>

          {/* Grid */}
          {filteredTerms.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-zinc-500 text-lg">
                {t.nada}
              </p>
              <button
                onClick={() => {
                  setSearch("")
                  setActiveCategory("all")
                }}
                className="mt-4 text-lime-600 font-medium hover:text-lime-700"
              >
                {t.limpiar}
              </button>
            </div>
          ) : (
            <div className="space-y-12">
              {alphabet
                .filter((letter) =>
                  filteredTerms.some(
                    (t) => t.term.charAt(0).toUpperCase() === letter
                  )
                )
                .map((letter) => (
                  <div key={letter} id={`letter-${letter}`}>
                    <h3 className="text-2xl font-bold text-zinc-900 mb-4 border-b border-zinc-200 pb-2">
                      {letter}
                    </h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {filteredTerms
                        .filter(
                          (t) => t.term.charAt(0).toUpperCase() === letter
                        )
                        .map((term) => {
                          const colors = categoryColors[term.category] || {
                            bg: "bg-zinc-100",
                            text: "text-zinc-600",
                          }
                          return (
                            <Link
                              key={term.clave}
                              href={term.href}
                              className="group bg-white rounded-2xl border border-zinc-200 hover:border-zinc-300 p-6 transition-all hover:shadow-lg hover:-translate-y-1"
                            >
                              <div className="flex items-start justify-between mb-3">
                                <h4 className="text-lg font-semibold text-zinc-900 group-hover:text-lime-600 transition-colors">
                                  {term.term}
                                </h4>
                                <span
                                  className={`px-2.5 py-1 rounded-full text-xs font-medium ${colors.bg} ${colors.text} shrink-0 ml-2`}
                                >
                                  {nombreDe(term.category)}
                                </span>
                              </div>
                              <p className="text-zinc-600 text-sm leading-relaxed mb-4 line-clamp-3">
                                {term.shortDefinition}
                              </p>
                              <div className="flex items-center gap-1.5 text-sm font-medium text-lime-600 group-hover:text-lime-700">
                                {t.leer}
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                              </div>
                            </Link>
                          )
                        })}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </section>
    </>
  )
}
